/**
 * Document type utilities for the frontend
 * This file provides functions for working with document types
 * and will be updated with data from the backend API
 */

export interface DocumentType {
  code: string;
  name_en: string;
  name_zh: string;
  description: string;
}

// Store for document types fetched from the backend
let documentTypes: DocumentType[] = [];

/**
 * Set the document types from the backend
 * @param types Array of document types from the backend
 */
export const setDocumentTypes = (types: DocumentType[]) => {
  documentTypes = types;
};

/**
 * Get all document types
 * @returns Array of document types
 */
export const getDocumentTypes = (): DocumentType[] => {
  return documentTypes;
};

/**
 * Get the display name for a document type code
 * @param code The document type code
 * @param language The language to use (en or zh)
 * @returns The display name in the specified language
 */
export const getDocumentTypeDisplayName = (code: string, language: 'en' | 'zh' = 'en'): string => {
  const docType = documentTypes.find(dt => dt.code === code);
  if (docType) {
    return language === 'zh' ? docType.name_zh : docType.name_en;
  }
  
  // Fallback to hardcoded mapping if types aren't loaded yet or type not found
  const typeMap: {[key: string]: {en: string, zh: string}} = {
    'BLOOD_TEST': {en: 'Blood Test Report', zh: '血液检查报告'},
    'OPTICAL_EXAM': {en: 'Optical Examination Report', zh: '眼科检查报告'},
    'MEDICAL_RECORD': {en: 'Medical Record', zh: '病历记录'},
    'PRESCRIPTION': {en: 'Prescription', zh: '处方单'},
    'IMAGING_REPORT': {en: 'Imaging Report', zh: '影像学报告'},
    'LAB_REPORT': {en: 'Laboratory Report', zh: '实验室检查报告'},
    'DISCHARGE_SUMMARY': {en: 'Discharge Summary', zh: '出院小结'},
    'PATHOLOGY_REPORT': {en: 'Pathology Report', zh: '病理报告'},
    'VACCINATION_RECORD': {en: 'Vaccination Record', zh: '疫苗接种记录'},
    'SURGICAL_REPORT': {en: 'Surgical Report', zh: '手术报告'},
    'OTHER': {en: 'Other Medical Document', zh: '其他医疗文档'}
  };
  
  return typeMap[code] ? typeMap[code][language] : code;
};

/**
 * Map an AI-determined document type to an internal code
 * @param aiType The document type determined by AI
 * @returns The internal code for the document type
 */
export const mapAiTypeToCode = (aiType: string): string => {
  if (!aiType) {
    return 'OTHER';
  }
  
  const aiTypeLower = aiType.toLowerCase();
  
  // Try to match by exact name first
  for (const docType of documentTypes) {
    if (docType.name_en.toLowerCase() === aiTypeLower || 
        docType.name_zh === aiType) {
      return docType.code;
    }
  }
  
  // If no exact match and we have keywords in the document types, try keyword matching
  // This is a simplified version of the backend logic
  const keywordMap: {[key: string]: string[]} = {
    'BLOOD_TEST': ['血', 'blood', '检验', 'laboratory', '血常规', '生化', '血细胞'],
    'OPTICAL_EXAM': ['眼', 'optical', '视力', 'vision', '眼底', '眼压'],
    'MEDICAL_RECORD': ['病历', 'record', '病史', 'history', '主诉', '诊断'],
    'PRESCRIPTION': ['处方', 'prescription', '药', 'medicine', '用法', '用量'],
    'IMAGING_REPORT': ['影像', 'imaging', 'X光', 'CT', '核磁', 'MRI', '超声'],
    'LAB_REPORT': ['实验室', 'laboratory', '检验', '尿', 'urine', '粪', 'stool'],
    'DISCHARGE_SUMMARY': ['出院', 'discharge', '小结', 'summary', '住院'],
    'PATHOLOGY_REPORT': ['病理', 'pathology', '活检', 'biopsy', '细胞', 'cell'],
    'VACCINATION_RECORD': ['疫苗', 'vaccine', '接种', 'vaccination', '免疫'],
    'SURGICAL_REPORT': ['手术', 'surgery', '术中', 'intraoperative', '麻醉']
  };
  
  for (const [code, keywords] of Object.entries(keywordMap)) {
    for (const keyword of keywords) {
      if (aiTypeLower.includes(keyword.toLowerCase())) {
        return code;
      }
    }
  }
  
  // Default to OTHER if no match found
  return 'OTHER';
}; 